import React, { useMemo } from "react";

const toLocalDate = (dateStr, timeStr = "00:00") => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const [hh = 0, mm = 0] = String(timeStr).split(":").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const fmtLeft = (ms) => {
  if (!ms || ms <= 0) return "00:00:00";
  let s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const RecordingIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    className="block"
    aria-hidden="true"
  >
    <path
      d="M15 10.5V7a2 2 0 0 0-2-2H5A2 2 0 0 0 3 7v10a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3.5l6 4v-11l-6 4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

export default function SartLesson({
  role = "teacher",
  date,
  startTime,
  endTime,
  status,
  serverNowMs,
  hasRecording = false,
  recordingUrl = "",
  recordings = [],
  canPlayRecording = false,
  isInCart = false,
  isPurchased = false,
  liveMeetingActive = false,
  teacherShouldContinue = false,
  teacherLessonInProgress = false,
  studentCanJoinFirstSegment = false,
  studentCanJoinNextSegment = false,
  studentLessonInProgress = false,
  onStart,
  onAddToCart,
  onShowRecording,
  className = "",
  starting = false,
})  {
  const startAt = useMemo(() => toLocalDate(date, startTime), [date, startTime]);
  const endAt = useMemo(() => toLocalDate(date, endTime), [date, endTime]);

  const now = useMemo(() => {
    if (serverNowMs == null) return null;
    const dt = new Date(serverNowMs);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }, [serverNowMs]);

  const logicalPhase = useMemo(() => {
    if (!startAt || !now) return "UNKNOWN";
    if (now < startAt) return "BEFORE";
    if (endAt && now > endAt) return "AFTER";
    return "LIVE";
  }, [now, startAt, endAt]);

  const normalizedStatus = String(status || "").toUpperCase().trim();

  const isCancelled =
    normalizedStatus === "CANCELLED" ||
    normalizedStatus === "CANCELED" ||
    normalizedStatus === "CANCEL";

  const isNoShow =
    normalizedStatus === "NO_SHOW" ||
    normalizedStatus === "MISSED" ||
    normalizedStatus === "NOT_HELD";

  const isCompleted =
    normalizedStatus === "COMPLETED" ||
    normalizedStatus === "DONE";

  const effectivePhase = useMemo(() => {
    if (isCancelled) return "CANCELLED";
    if (isNoShow) return "NO_SHOW";
    if (isCompleted) return "AFTER";
    return logicalPhase;
  }, [isCancelled, isNoShow, isCompleted, logicalPhase]);

  const isTeacher = role === "teacher";
  const isStudent = role === "student";

  const effectiveRecordings = useMemo(() => {
    if (Array.isArray(recordings) && recordings.length > 0) return recordings;
    if (recordingUrl) return [{ recordingUrl }];
    return [];
  }, [recordings, recordingUrl]);

  const hasAnyRecording = hasRecording || effectiveRecordings.length > 0;

  const openInNewTab = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStart = () => {
    onStart?.();
  };

  const handleRecording = () => {
    if (onShowRecording) onShowRecording(effectiveRecordings);
    else if (effectiveRecordings[0]?.recordingUrl) openInNewTab(effectiveRecordings[0].recordingUrl);
  };

  const base =
    "inline-flex items-center justify-center gap-3 rounded-lg px-4 py-2.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed";
  const variants = {
    primary:
      "bg-indigo-600 text-white hover:bg-indigo-700 focus-visible:ring-indigo-300",
    success:
      "bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-300",
    warning:
      "bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-300",
    info:
      "bg-sky-600 text-white hover:bg-sky-700 focus-visible:ring-sky-300",
    danger:
      "bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-300",
    muted:
      "bg-gray-300 text-gray-700",
    ghost:
      "bg-gray-100 text-gray-600",
  };

  let label = "—";
  let style = variants.muted;
  let disabled = true;
  let onClick;
  let icon = null;

  if (isTeacher) {
    switch (effectivePhase) {
      case "CANCELLED":
        label = "השיעור התבטל";
        style = variants.danger;
        break;

      case "NO_SHOW":
        label = "השיעור לא התקיים";
        style = variants.muted;
        break;

      case "BEFORE":
        label = `מתחיל בעוד ${fmtLeft(
          startAt && now ? startAt.getTime() - now.getTime() : 0
        )}`;
        style = variants.warning;
        break;

      case "LIVE":
        if (teacherLessonInProgress) {
          label = "השיעור בביצוע";
          style = variants.success;
          disabled = false;
          onClick = handleStart;
        } else if (teacherShouldContinue) {
          label = "המשך שיעור";
          style = variants.warning;
          disabled = false;
          onClick = handleStart;
        } else {
          label = "התחל שיעור";
          style = variants.primary;
          disabled = false;
          onClick = handleStart;
        }
        break;

      case "AFTER":
        if (hasAnyRecording && canPlayRecording) {
          label = "צפייה בהקלטה";
          style = variants.success;
          disabled = false;
          onClick = handleRecording;
          icon = <RecordingIcon />;
        } else {
          label = "השיעור הסתיים";
          style = variants.muted;
        }
        break;

      default:
        label = "—";
        style = variants.ghost;
    }
  }

  if (isStudent) {
    switch (effectivePhase) {
      case "CANCELLED":
        label = "השיעור התבטל";
        style = variants.danger;
        break;

      case "NO_SHOW":
        label = "השיעור לא התקיים";
        style = variants.muted;
        break;

      case "BEFORE":
        if (isPurchased) {
          label = `מתחיל בעוד ${fmtLeft(
            startAt && now ? startAt.getTime() - now.getTime() : 0
          )}`;
          style = variants.warning;
        } else if (isInCart) {
          label = "בתוך הסל";
          style = variants.muted;
        } else {
          label = "הוסף לסל";
          style = variants.info;
          disabled = false;
          onClick = onAddToCart;
        }
        break;

      case "LIVE":
        if (!isPurchased) {
          if (isInCart) {
            label = "בתוך הסל";
            style = variants.muted;
          } else {
            label = "נדרש תשלום";
            style = variants.warning;
          }
        } else if (studentLessonInProgress) {
          label = "השיעור בביצוע";
          style = variants.success;
          disabled = false;
          onClick = handleStart;
        } else if (studentCanJoinNextSegment) {
          label = "המשך שיעור";
          style = variants.warning;
          disabled = false;
          onClick = handleStart;
        } else if (studentCanJoinFirstSegment) {
          label = "התחל שיעור";
          style = variants.primary;
          disabled = false;
          onClick = handleStart;
        } else {
          label = "ממתין למורה";
          style = variants.muted;
        }
        break;

      case "AFTER":
        if (hasAnyRecording) {
          if (canPlayRecording) {
            label = "צפייה בהקלטה";
            style = variants.success;
            disabled = false;
            onClick = handleRecording;
            icon = <RecordingIcon />;
          } else if (isInCart) {
            label = "בתוך הסל";
            style = variants.muted;
          } else {
            label = "הוסף לסל";
            style = variants.info;
            disabled = false;
            onClick = onAddToCart;
          }
        } else {
          label = "השיעור הסתיים";
          style = variants.muted;
        }
        break;

      default:
        label = "—";
        style = variants.ghost;
    }
  }

  const isDisabled = disabled || starting;

  const finalLabel =
    starting &&
    (label === "התחל שיעור" ||
      label === "המשך שיעור" ||
      label === "השיעור בביצוע")
      ? "מתחברים לשיעור..."
      : label;

  return (
    <button
      type="button"
      className={`${base} ${style} ${className}`}
      onClick={onClick}
      disabled={isDisabled}
      dir="rtl"
      aria-label={finalLabel}
      title={finalLabel}
    >
      {starting ? (
        <span className="flex items-center justify-center gap-2">
          <svg
            className="animate-spin h-4 w-4 shrink-0 block"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8h4z"
            />
          </svg>
          <span className="block leading-none">{finalLabel}</span>
        </span>
      ) : (
        <span className="flex items-center justify-center gap-2">
          {icon ? (
            <span className="flex h-4 w-4 items-center justify-center shrink-0 translate-y-[1px]">
              {icon}
            </span>
          ) : null}
          <span className="block leading-none">{finalLabel}</span>
        </span>
      )}
    </button>
  );
}